import React from "react";

import { cn } from "@/lib/utils";

export type BannerVariant = "info" | "warning" | "error";

const VARIANT_CLASSES: Record<
    BannerVariant,
    { wrapper: string; accent: string; iconWrapper: string; title: string; description: string }
> = {
    info: {
        wrapper: "border-[var(--info-border)] bg-[var(--info-bg)]",
        accent: "bg-[var(--info)]",
        iconWrapper: "border-[var(--info-icon-border)] bg-[var(--info-icon-bg)] text-[var(--info-text)]",
        title: "text-foreground",
        description: "text-[var(--info-text)]",
    },
    warning: {
        wrapper: "border-[var(--warning-border)] bg-[var(--warning-bg)]",
        accent: "bg-[var(--warning)]",
        iconWrapper: "border-[var(--warning-icon-border)] bg-[var(--warning-icon-bg)] text-[var(--warning-text)]",
        title: "text-foreground",
        description: "text-[var(--warning-text)]",
    },
    error: {
        wrapper: "border-[var(--error-border)] bg-[var(--error-bg)]",
        accent: "bg-[var(--error)]",
        // `--error-text`, not the raw `--error` (ERRORS-16, ported 2026-08-23). `--error` is declared
        // once and deliberately NEVER redefined in `.dark` (see 00-variables.css:25-45), while
        // `--error-icon-bg` IS — so the chip flipped with the theme and the glyph on it did not,
        // landing at 2.02:1 in dark against the 3:1 SC 1.4.11 sets for meaningful non-text. The
        // `description` below has always taken the theme-corrected sibling; this is that one-caller
        // port, not a token change. Computed from the token graph: 12.49:1 light, 8.84:1 dark.
        // The raw `--error` stays on the accent bar and the wrapper border, where it reads correctly
        // in both themes and where `destructiveTokenContrast.spec.ts` measures it as a fill.
        // A91 (ERRORS-48, 2026-09-28) ported `info` and `warning` the same way: in dark the raw `--info`
        // glyph measured 2.81:1 on its own chip on every 404, and the server floor in `custom-error.jte`
        // already painted `--info-text`, so the island LOWERED contrast at mount (rule 32 in miniature).
        iconWrapper: "border-[var(--error-icon-border)] bg-[var(--error-icon-bg)] text-[var(--error-text)]",
        title: "text-foreground",
        description: "text-[var(--error-text)]",
    },
};

export function Banner(props: {
    variant: BannerVariant;
    icon?: React.ElementType;
    title?: React.ReactNode;
    description: React.ReactNode;
    className?: string;
}) {
    const { variant, icon: Icon, title, description, className } = props;
    const variantClasses = VARIANT_CLASSES[variant];

    return (
        // Flat-X rule 1: the 1px variant border is the elevation. A Banner sits in page/form flow and
        // never floats, so neither it nor its icon chip carries a resting drop-shadow. `text-foreground`
        // rather than `text-[var(--text-main)]`: same colour, but the semantic name is the one the token
        // table owns and the only one that survives a token rename. Guard: `Banner.spec.tsx`.
        <div className={cn("relative w-full overflow-hidden rounded-2xl border px-4 py-4 text-foreground sm:px-5 sm:py-5", variantClasses.wrapper, className)}>
            <div className={cn("absolute inset-y-0 left-0 w-1.5", variantClasses.accent)} aria-hidden="true" />

            {/* One alignment axis at every width, and ONE ROW at every width (U62, DESIGN-SYSTEM-13, 2026-09-28).
                Below `sm` this used to be `flex-col`: the 44px icon tile took a line of its own above the
                title, about a quarter of every phone banner's height, pushing the member's task down. The
                row now holds at every width, and the tile steps down to 36px below `sm` so it sits on the
                title line rather than towering over it. The `items-start` axis from the 2026-08 fix is
                kept: the tile aligns with the title's first line, the copy column keeps its own left edge.
                Guard: `Banner.spec.tsx`. */}
            <div className="flex flex-row items-start gap-3 pl-2 sm:gap-4">
                {Icon ? (
                    <div data-slot="banner-icon" className={cn("flex size-9 shrink-0 items-center justify-center rounded-2xl border sm:mt-0.5 sm:size-11", variantClasses.iconWrapper)}>
                        <Icon className="size-4 sm:size-5" aria-hidden="true" />
                    </div>
                ) : null}

                <div className="min-w-0 w-full flex-1 ">
                    {/* No text-alignment class: the title inherits the block's inline-start axis, which
                        is the axis the description already had. Two stacked pieces of copy in one column
                        must not disagree about it.

                        This wrapper is a `<div>` on purpose and must stay one. `title` is a node, so a
                        caller whose banner IS the page — `CustomErrorPage`, which renders an `<h1>` here
                        — supplies its own heading element, while the info and warning banners that sit
                        inside pages already owning an `h1` supply plain text. Baking a heading level in
                        here would put a second heading of that level on every one of those pages
                        (ERRORS-3, 2026-08-14). */}
                    {title ? <div className={cn("text-base font-semibold leading-6 tracking-tight", variantClasses.title)}>{title}</div> : null}
                    <div className={cn(title ? "mt-1.5 text-sm leading-6" : "text-sm leading-6", variantClasses.description)}>{description}</div>
                </div>
            </div>
        </div>
    );
}
